import React, { useRef, useState } from "react";
import {
  TEXT_ANNOTATION_MAX_WIDTH,
  TEXT_ANNOTATION_MIN_WIDTH,
} from "../constants";
import { getTextAnnotationMetrics } from "../utils";

const EDGE = 6;

const TextAnnotationInput = ({
  textInput,
  textInputPos,
  inputWidth,
  inputHeight,
  fontSize,
  color,
  textBold,
  textItalic,
  textUnderline,
  textStrike,
  inputRef,
  zoom = 1,
  onTextChange,
  onBlur,
  onKeyDown,
  onBoxResize,
  onBoxMove,
  onResizeStart,
  onResizeEnd,
  onMoveStart,
  onMoveEnd,
}) => {
  const resizingRef = useRef(false);
  const movingRef = useRef(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  if (!textInputPos) return null;

  const width = Math.max(inputWidth || TEXT_ANNOTATION_MIN_WIDTH, TEXT_ANNOTATION_MIN_WIDTH);
  const height = Math.max(inputHeight || fontSize * 1.5, fontSize * 1.5);
  const lineHeight = fontSize * 1.3;
  const safeZoom = Math.max(zoom || 1, 0.1);

  const handleResizePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = true;
    setIsResizing(true);
    onResizeStart?.();

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = width;
    const startH = height;
    // Anchor top-left so SE resize only moves the right/bottom edges.
    const left = textInputPos.x - startW / 2;
    const top = textInputPos.y - startH / 2;
    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / safeZoom;
      const dy = (ev.clientY - startY) / safeZoom;
      const nextWidth = Math.min(
        TEXT_ANNOTATION_MAX_WIDTH,
        Math.max(TEXT_ANNOTATION_MIN_WIDTH, startW + dx),
      );
      const contentHeight = getTextAnnotationMetrics(
        textInput || " ",
        fontSize,
        nextWidth,
      ).height;
      const nextHeight = Math.max(
        fontSize * 1.5,
        startH + dy,
        contentHeight + 8,
      );
      onBoxResize?.({
        width: nextWidth,
        height: nextHeight,
        position: {
          x: left + nextWidth / 2,
          y: top + nextHeight / 2,
        },
      });
    };

    const onUp = () => {
      resizingRef.current = false;
      setIsResizing(false);
      onResizeEnd?.();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const handleMovePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    movingRef.current = true;
    setIsMoving(true);
    onMoveStart?.();

    const startX = e.clientX;
    const startY = e.clientY;
    const origin = { x: textInputPos.x, y: textInputPos.y };

    const onMove = (ev) => {
      const dx = (ev.clientX - startX) / safeZoom;
      const dy = (ev.clientY - startY) / safeZoom;
      onBoxMove?.({ x: origin.x + dx, y: origin.y + dy });
    };

    const onUp = () => {
      movingRef.current = false;
      setIsMoving(false);
      onMoveEnd?.();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const edgeStyle = {
    position: "absolute",
    zIndex: 2,
    cursor: isMoving ? "grabbing" : "grab",
    background: "transparent",
  };

  return (
    <foreignObject
      x={textInputPos.x - width / 2}
      y={textInputPos.y - height / 2}
      width={width + 12}
      height={height + 12}
      overflow="hidden"
    >
      <div
        style={{
          position: "relative",
          width,
          height,
          border: "2px solid #1890ff",
          borderRadius: 4,
          background: "white",
          boxSizing: "border-box",
          transform: "translateZ(0)",
        }}
      >
        <textarea
          ref={inputRef}
          rows={1}
          style={{
            width: "100%",
            height: "100%",
            fontSize,
            lineHeight: `${lineHeight}px`,
            border: "none",
            outline: "none",
            borderRadius: 2,
            background: "transparent",
            caretColor: isResizing ? "transparent" : "#1890ff",
            color: color,
            padding: "2px 4px",
            margin: 0,
            resize: "none",
            overflow: "hidden",
            // Keep only user-entered newlines in the value. Browser soft-wraps
            // visually, while SVG/PDF apply the shared wrap algorithm on render.
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
            wordBreak: "normal",
            boxSizing: "border-box",
            textAlign: "left",
            fontFamily: "Arial, sans-serif",
            fontWeight: textBold ? "bold" : "normal",
            fontStyle: textItalic ? "italic" : "normal",
            textDecoration: `${
              textUnderline ? "underline " : ""
            }${textStrike ? "line-through" : ""}`.trim(),
            cursor: "text",
          }}
          value={textInput}
          onChange={onTextChange}
          onBlur={(e) => {
            if (resizingRef.current || movingRef.current) return;
            onBlur?.(e);
          }}
          onKeyDown={onKeyDown}
        />
        <div
          onPointerDown={handleMovePointerDown}
          style={{ ...edgeStyle, top: -2, left: 0, right: 14, height: EDGE }}
          title="Drag to move"
        />
        <div
          onPointerDown={handleMovePointerDown}
          style={{ ...edgeStyle, bottom: -2, left: 0, right: 14, height: EDGE }}
          title="Drag to move"
        />
        <div
          onPointerDown={handleMovePointerDown}
          style={{ ...edgeStyle, top: 0, left: -2, bottom: 14, width: EDGE }}
          title="Drag to move"
        />
        <div
          onPointerDown={handleMovePointerDown}
          style={{ ...edgeStyle, top: 0, right: -2, bottom: 14, width: EDGE }}
          title="Drag to move"
        />
        <div
          onPointerDown={handleResizePointerDown}
          style={{
            position: "absolute",
            right: -2,
            bottom: -2,
            width: 14,
            height: 14,
            zIndex: 3,
            cursor: "nwse-resize",
            background:
              "linear-gradient(135deg, transparent 50%, #1890ff 50%)",
            borderBottomRightRadius: 4,
          }}
        />
      </div>
    </foreignObject>
  );
};

export default TextAnnotationInput;
